import {
  ChevronDown,
  ChevronUp,
  Payment,
  Withdrawal,
} from "@/components/bnp-icons";
import { Checkbox } from "@mantine/core";
import React, { useEffect, useState, useRef } from "react";
import PropertyTable from "../property-table";
import moment from "moment";
import { safeMoment } from "@/utils/safeMoment";

import Image from "next/image";
import { useQuery } from "@tanstack/react-query";
import { LiveInspectionService, PropertyService } from "@/services";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import parseHttpError from "@/utils/parseHttpError";
import { NoSubscriptions } from "@/components/bnp-illustrations";
import { Pagination } from "@/components/bnp-ui";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";

const SortIndicator = ({
  active,
  direction,
}: {
  active: boolean;
  direction: "asc" | "desc" | null;
}) => {
  return (
    <svg
      width="10"
      height="14"
      viewBox="0 0 10 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="ml-1"
    >
      <path
        d="M9.16732 4.91667L5.00065 0.75L0.833984 4.91667H9.16732Z"
        fill={active && direction === "asc" ? "#2D96A7" : "#868C98"}
      />
      <path
        d="M9.16732 9.08333L5.00065 13.25L0.833984 9.08333H9.16732Z"
        fill={active && direction === "desc" ? "#2D96A7" : "#868C98"}
      />
    </svg>
  );
};

const getStatusStyle = (status: any) => {
  switch (status.toLowerCase()) {
    // case "approved":
    //   return "bg-green-success text-main-900";
    case "pending":
      return "bg-[#FEF3EB] text-[#CC7914]";
    case "rejected":
      return "bg-[#F8C9D2] text-[#8C1823]";
    case "rented":
      return "bg-[#E7EBEF] text-[#1C7B44]";
    case "available":
      return "bg-[#CBF5E5] text-[#1C7B44]";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const StatusDropdown = ({ listing, onStatusChange }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isDisabled =
    listing?.status === "pending" || listing?.status === "rejected";

  const statusOptions = [
    // "Approved",
    // "Pending",
    // "Rejected",
    "Available",
    ...(listing?.listing_type === "rent" || listing?.listing_type === "shortlet"
      ? ["Rented"]
      : []),
  ];

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      //@ts-ignore
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStatusSelect = (status: any) => {
    onStatusChange(listing.id, status.toLowerCase());
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        className={`flex items-center justify-between font-medium p-2 gap-1 w-[150px] h-[40px] text-sm border border-soft-200 rounded-lg ${
          isDisabled
            ? "bg-weak-100 text-grey-7 cursor-not-allowed opacity-70"
            : "text-main-900 hover:text-bnp-cyan cursor-pointer"
        }`}
        disabled={isDisabled}
      >
        <span>{isDisabled ? "Status locked" : "Change status"}</span>
        {!isDisabled && (
          <ChevronDown
            className={`w-4 h-4 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {isOpen && !isDisabled && (
        <div className="absolute z-10 mt-1 w-[150px] bg-white border border-soft-200 rounded-lg shadow-lg">
          {statusOptions.map((status) => (
            <div
              key={status}
              className="px-4 py-2 hover:bg-weak-100 cursor-pointer text-main-900 text-sm"
              onClick={() => handleStatusSelect(status)}
            >
              {status}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
const LiveInspectionTable = ({ listingType }: any) => {
  const router = useRouter();
  const [sortField, setSortField] = useState<string | null>(null);
  const [properties, setProperties] = useState<any>([]);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;
  const { page } = router.query;

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Apply sorting to properties
  const getSortedProperties = () => {
    if (!sortField) return properties;

    return [...properties].sort((a, b) => {
      let valA, valB;

      if (sortField === "date") {
        valA = new Date(a.created_at).getTime();
        valB = new Date(b.created_at).getTime();
      } else if (sortField === "listing") {
        valA = a.description.toLowerCase();
        valB = b.description.toLowerCase();
      } else {
        return 0;
      }

      if (sortDirection === "asc") {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
  };

  const handleStatusChange = async (propertyId: string, newStatus: string) => {
    try {
      // Update status locally first for immediate UI feedback
      setProperties(
        properties.map((property: any) =>
          property.id === propertyId
            ? { ...property, status: newStatus }
            : property,
        ),
      );

      // Here you would typically make an API call to update the status
      // For example:
      // await PropertyService.UpdatePropertyStatus(propertyId, newStatus);

      // Optionally refetch data to ensure UI is in sync with server
      // propertiesRefetch();
    } catch (error) {
      console.error("Failed to update status:", error);
      // Revert to original status if API call fails
      propertiesRefetch();
    }
  };

  const getPropertiesRequest = async () => {
    try {
      const response = await LiveInspectionService.GetUserLiveInspections({
        pageNumber: currentPage,
        pageSize,
      });
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: propertiesData,
    isLoading: propertiesIsLoading,
    isError: propertiesIsError,
    refetch: propertiesRefetch,
    isSuccess: propertiesIsSuccess,
  } = useQuery({
    queryKey: ["liveinspections", listingType, page],
    queryFn: getPropertiesRequest,
    staleTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (propertiesData) {
      setProperties(propertiesData?.data?.results);
    }
  }, [propertiesData]);

  useEffect(() => {
    // Re-sort properties whenever sortField or sortDirection changes
    // This isn't strictly necessary since getSortedProperties() is called in the render,
    // but it keeps the sorted data in state which can be useful for other operations
    if (sortField) {
      setProperties(getSortedProperties());
    }
  }, [sortField, sortDirection]);

  const handlePageChange = (pageNumber: number) => {
    const newQuery = {
      ...router.query,
      page: pageNumber.toString(),
    };

    router.push({
      pathname: router.pathname,
      query: newQuery,
    });
  };

  useEffect(() => {
    if (page) {
      setCurrentPage(Number(page));
    } else {
      setCurrentPage(1);
    }
  }, [page]);

  // Get sorted properties for rendering
  const sortedProperties = getSortedProperties();

  return (
    <div className="w-[100%] lg:w-[950px]">
      <div className="">
        <table className="w-[100%] lg:w-[950px] border-collapse">
          <thead className="rounded-[24px] w-[100%] max-w-[100%]">
            <tr className="text-grey-7 bg-weak-100 rounded-[24px]">
              <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("date")}
              >
                <span className="inline-flex items-center font-normal">
                  Date
                  <SortIndicator
                    active={sortField === "date"}
                    direction={sortField === "date" ? sortDirection : null}
                  />
                </span>
              </th>
              <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("listing")}
              >
                <span className="inline-flex items-center font-normal">
                  Listing
                  <SortIndicator
                    active={sortField === "listing"}
                    direction={sortField === "listing" ? sortDirection : null}
                  />
                </span>
              </th>
              <th className="p-4 text-left">Report</th>
              <th className="p-4 text-left">Status</th>
              {/* <th
                className="p-4 text-left cursor-pointer"
                onClick={() => handleSort("status")}
              >
                <span className="inline-flex items-center font-normal">
                  Status
                </span>
              </th> */}
            </tr>
          </thead>
          <tbody>
            {propertiesIsLoading ? (
              <tr>
                <td colSpan={4}>
                  <div className="flex flex-col justify-center items-center w-full h-fit p-8">
                    <div className="w-fit h-fit">
                      <Image
                        src={"/assets/images/property-default.svg"}
                        width={200}
                        height={200}
                        alt={"property"}
                        className="w-[400px] h-[400px] object-cover animate-pulse"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {sortedProperties?.length > 0 ? (
                  <>
                    {sortedProperties.map((inspection: any, index: any) => {
                      const { property, document_url, status, created_at } =
                        inspection;
                      //alert(document_url);
                      return (
                        <tr
                          key={index}
                          className="border-b cursor-pointer border-soft-200 bg-white hover:bg-grey-6 transition-colors ease-linear delay-150"
                        >
                          <td className="p-4">
                            <div className="text-sm text-grey-7">
                              {safeMoment(property?.created_at)?.format(
                                "DD MMMM, YYYY",
                              )}
                            </div>
                          </td>

                          <td className="p-4">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-12 overflow-hidden rounded-md">
                                <img
                                  src={
                                    property?.image_url?.length > 0
                                      ? property?.image_url[0]
                                      : "/assets/images/property-default.svg"
                                  }
                                  alt={"property"}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div
                                onClick={() => {
                                  router.push(`/property/${property?.id}`);
                                }}
                              >
                                <h3 className="text-main-900 hover:text-bnp-cyan text-base min-w-[274px] max-w-[274px] truncate font-bold">
                                  {property?.description}
                                </h3>
                                <div className="flex items-center gap-2 text-grey-7 text-sm mt-1">
                                  <div className="flex items-center gap-1">
                                    <svg
                                      width="9"
                                      height="12"
                                      viewBox="0 0 9 12"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M0.0117188 11.2516C0.0117188 10.1377 0.454218 9.06937 1.24187 8.28171C2.02952 7.49406 3.09781 7.05156 4.21172 7.05156C5.32563 7.05156 6.39391 7.49406 7.18157 8.28171C7.96922 9.06937 8.41172 10.1377 8.41172 11.2516H7.36172C7.36172 10.4161 7.02984 9.61491 6.43911 9.02418C5.84837 8.43344 5.04715 8.10156 4.21172 8.10156C3.37629 8.10156 2.57507 8.43344 1.98433 9.02418C1.39359 9.61491 1.06172 10.4161 1.06172 11.2516H0.0117188ZM4.21172 6.52656C2.47134 6.52656 1.06172 5.11694 1.06172 3.37656C1.06172 1.63619 2.47134 0.226562 4.21172 0.226562C5.95209 0.226562 7.36172 1.63619 7.36172 3.37656C7.36172 5.11694 5.95209 6.52656 4.21172 6.52656ZM4.21172 5.47656C5.37197 5.47656 6.31172 4.53681 6.31172 3.37656C6.31172 2.21631 5.37197 1.27656 4.21172 1.27656C3.05147 1.27656 2.11172 2.21631 2.11172 3.37656C2.11172 4.53681 3.05147 5.47656 4.21172 5.47656Z"
                                        fill="#2D96A7"
                                      />
                                    </svg>

                                    <span className="max-w-[132px] truncate">
                                      {`${property?.realtor?.first_name} ${property?.realtor?.last_name}`}
                                    </span>
                                  </div>
                                  <span>•</span>
                                  <div className="flex items-center gap-1">
                                    <svg
                                      width="15"
                                      height="14"
                                      viewBox="0 0 15 14"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M7.21133 11.6725L9.81008 9.07371C10.324 8.55974 10.674 7.90491 10.8157 7.19204C10.9575 6.47917 10.8847 5.74027 10.6066 5.06878C10.3284 4.39728 9.85735 3.82334 9.25301 3.41955C8.64867 3.01575 7.93816 2.80022 7.21133 2.80022C6.4845 2.80022 5.77399 3.01575 5.16965 3.41955C4.56531 3.82334 4.09427 4.39728 3.81611 5.06878C3.53795 5.74027 3.46515 6.47917 3.60692 7.19204C3.74869 7.90491 4.09866 8.55974 4.61258 9.07371L7.21133 11.6725ZM7.21133 13.1572L3.87023 9.81606C3.20943 9.15525 2.75943 8.31334 2.57712 7.39678C2.39481 6.48022 2.48838 5.53018 2.846 4.66681C3.20363 3.80343 3.80925 3.06549 4.58627 2.5463C5.36329 2.02711 6.27682 1.75 7.21133 1.75C8.14584 1.75 9.05937 2.02711 9.83639 2.5463C10.6134 3.06549 11.219 3.80343 11.5767 4.66681C11.9343 5.53018 12.0279 6.48022 11.8455 7.39678C11.6632 8.31334 11.2132 9.15525 10.5524 9.81606L7.21133 13.1572ZM7.21133 7.52496C7.48981 7.52496 7.75688 7.41433 7.95379 7.21742C8.1507 7.02051 8.26133 6.75344 8.26133 6.47496C8.26133 6.19648 8.1507 5.92941 7.95379 5.7325C7.75688 5.53558 7.48981 5.42496 7.21133 5.42496C6.93285 5.42496 6.66578 5.53558 6.46887 5.7325C6.27195 5.92941 6.16133 6.19648 6.16133 6.47496C6.16133 6.75344 6.27195 7.02051 6.46887 7.21742C6.66578 7.41433 6.93285 7.52496 7.21133 7.52496ZM7.21133 8.57496C6.65438 8.57496 6.12023 8.35371 5.72641 7.95988C5.33258 7.56606 5.11133 7.03191 5.11133 6.47496C5.11133 5.918 5.33258 5.38386 5.72641 4.99003C6.12023 4.59621 6.65438 4.37496 7.21133 4.37496C7.76828 4.37496 8.30243 4.59621 8.69625 4.99003C9.09008 5.38386 9.31133 5.918 9.31133 6.47496C9.31133 7.03191 9.09008 7.56606 8.69625 7.95988C8.30243 8.35371 7.76828 8.57496 7.21133 8.57496Z"
                                        fill="#2D96A7"
                                      />
                                    </svg>

                                    <span className="text-sm text-grey-7 min-w-[191px] max-w-[191px] truncate">
                                      {property?.address}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          {/* Report */}
                          <td className="p-4">
                            {document_url ? (
                              <a
                                href={document_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-bnp-cyan hover:underline"
                              >
                                View Report
                              </a>
                            ) : (
                              <span className="text-grey-5 italic">
                                No report
                              </span>
                            )}
                          </td>

                          {/* Status */}
                          <td className="p-4">
                            <div className="flex items-center gap-6">
                              <span
                                className={`flex gap-1 items-center px-3 py-1 min-w-[105px] rounded-lg text-sm ${getStatusStyle(
                                  status,
                                )}`}
                              >
                                <svg
                                  width="5"
                                  height="4"
                                  viewBox="0 0 5 4"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <circle
                                    cx="2.21094"
                                    cy="2"
                                    r="2"
                                    fill="currentColor"
                                  />
                                </svg>
                                {capitalizeFirstLetter(status)}
                              </span>

                              {/* Status dropdown for changing status */}
                            </div>
                          </td>

                          {/* <td className="p-4">
                            <div className="flex items-center gap-6">
                              <span
                                className={`flex gap-1 items-center px-3 py-1 min-w-[105px] rounded-lg text-sm ${getStatusStyle(
                                  property?.status
                                )}`}
                              >
                                <svg
                                  width="5"
                                  height="4"
                                  viewBox="0 0 5 4"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <circle
                                    cx="2.21094"
                                    cy="2"
                                    r="2"
                                    fill="currentColor"
                                  />
                                </svg>
                                {capitalizeFirstLetter(listing?.status)}
                              </span>

                              <StatusDropdown
                                listing={listing}
                                onStatusChange={handleStatusChange}
                              />
                            </div>
                          </td> */}
                        </tr>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="self-center mt-16 ml-auto mr-auto w-full justify-center items-center flex flex-col"
                    >
                      <NoSubscriptions />
                      <p className="text-grey-7 lg:w-[408px] text-center mt-4 text-lg">
                        No properties found
                      </p>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {properties?.length > 0 && (
        <Pagination
          metadata={propertiesData?.data?.metadata}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          className="w-full mt-8"
        />
      )}
    </div>
  );
};

export default LiveInspectionTable;
